// WHAT IS THIS FILE? — binding a HOLDING line to a `ba_cbam_funds` row, exactly or not at all.
//
// The roster is INJECTED (see ClassifyOptions.fundRoster) so this module never touches Supabase
// and can be tested against a frozen snapshot of the table.
//
// ⚠️ THE RULE: EXACT-AFTER-NORMALISATION, OR NOTHING. ⚠️
//
// `ba_cbam_funds` stores a fund's identity in three inconsistent shapes, so we index each row
// under several keys and require the FE name to land on one of them exactly:
//   fe_fund_name        "Fidelity : Index US : P"        (colon form)
//   manager+fund+class  "Fidelity" + "Fidelity Index US" + "P Acc"
//   manager+fund        "L&G" + "Cash Trust"
//   fund+class          "Vanguard US Government Bond Index" + "Hedged Dis GBP"
//   fund                "Pictet China Index"
//
// What we do NOT do is fall back to token overlap when none of those hit. TRAP 7 is the reason.
// `ba_trades` carries an orphan row `fund_name_override = 'Fidelity : Japan : W Acc'` with
// `fund_id` NULL. A matcher that scored "Fidelity"+"Japan" would happily bind that orphan to
// `Fidelity Index Japan` (ISIN GB00BHZK8872) — a DIFFERENT fund, held separately in every book —
// and mint a position that never existed. An unmatched holding surfaces as a warning a human
// resolves in seconds. A wrongly matched holding is a number nobody can see is wrong.
//
// Consequence, and it is the correct one: on the 2026-07 export nine holdings come back unmatched
// (Fidelity Cash, L&G Commodity Index, L&G Global Infrastructure Index, PIMCO EM Opportunities,
// Royal London Short Duration Gilts, Vanguard UK Inflation-Linked Gilt, ...) because FE's share
// class contradicts the DB's or the DB stores an abbreviation. Every one of those is exactly the
// "class in doubt" set adjudicated in FE-EXPORT-CLASSIFICATION-2026-07.md §1.7. Reporting them is
// the point.

import { joinNameParts, normaliseKey } from "./normalise";
import type { CbamFundRosterRow, HoldingInfo, HoldingMatchKey } from "./types";

interface IndexedEntry {
  row: CbamFundRosterRow;
  key: HoldingMatchKey;
}

export interface FundRosterIndex {
  /** normalised key -> every roster row that claims it, in roster order. */
  byKey: Map<string, IndexedEntry[]>;
  size: number;
}

/**
 * Keys are tried in this order. Earlier = more specific = more trustworthy, so a hit on
 * `fe_fund_name` beats a hit on a bare `fund_name`.
 */
const KEY_ORDER: readonly HoldingMatchKey[] = [
  "fe_fund_name",
  "manager_fund_class",
  "manager_fund",
  "fund_class",
  "fund_name",
];

export function buildFundRosterIndex(roster: readonly CbamFundRosterRow[]): FundRosterIndex {
  const byKey = new Map<string, IndexedEntry[]>();

  const add = (raw: string, row: CbamFundRosterRow, key: HoldingMatchKey) => {
    if (!raw) return;
    const list = byKey.get(raw);
    // A row must not claim the same key twice under two different key-kinds; keep the first
    // (most specific) claim so `matchedOn` stays honest.
    if (list) {
      if (list.some((e) => e.row.id === row.id)) return;
      list.push({ row, key });
    } else {
      byKey.set(raw, [{ row, key }]);
    }
  };

  for (const row of roster) {
    add(normaliseKey(row.fe_fund_name), row, "fe_fund_name");
    add(joinNameParts(row.fund_manager, row.fund_name, row.fund_class), row, "manager_fund_class");
    add(joinNameParts(row.fund_manager, row.fund_name), row, "manager_fund");
    add(joinNameParts(row.fund_name, row.fund_class), row, "fund_class");
    add(normaliseKey(row.fund_name), row, "fund_name");
  }

  return { byKey, size: roster.length };
}

/**
 * Bind one suffix-stripped FE name.
 *
 * Ambiguity handling: when a key is claimed by more than one roster row, prefer `is_current`.
 * If exactly one current row remains the bind is deterministic and `ambiguous` stays false only
 * when there was never a contest at all — otherwise it is flagged so a human sees it. The live
 * example is "Royal London Short Dur Gilts M Inc", which exists twice under two ISINs
 * (GB00BD050C73 current, GB00BD050D80 not) and cannot be told apart from a name-only export.
 */
export function matchHolding(
  normalisedName: string,
  index: FundRosterIndex | null,
): HoldingInfo {
  const miss: HoldingInfo = {
    fundId: null,
    isin: null,
    matchedOn: null,
    ambiguous: false,
    candidateFundIds: [],
  };
  if (!index) return miss;

  const key = normaliseKey(normalisedName);
  if (!key) return miss;

  const entries = index.byKey.get(key);
  if (!entries || entries.length === 0) return miss;

  // Rank by key specificity first, then prefer a current row.
  const ranked = entries.slice().sort((a, b) => {
    const ka = KEY_ORDER.indexOf(a.key);
    const kb = KEY_ORDER.indexOf(b.key);
    if (ka !== kb) return ka - kb;
    const ca = a.row.is_current === true ? 0 : 1;
    const cb = b.row.is_current === true ? 0 : 1;
    return ca - cb;
  });

  const distinctIds = Array.from(new Set(ranked.map((e) => e.row.id)));
  const winner = ranked[0];
  const currentIds = ranked.filter((e) => e.row.is_current === true).map((e) => e.row.id);
  const distinctCurrent = Array.from(new Set(currentIds));

  // Ambiguous when more than one DISTINCT fund claims the key and `is_current` does not settle it
  // down to one. Two rows for the same fund (an old and a new spelling of the same ISIN) is not
  // ambiguity.
  const ambiguous = distinctIds.length > 1 && distinctCurrent.length !== 1;

  const chosen =
    distinctIds.length > 1 && distinctCurrent.length === 1
      ? (ranked.find((e) => e.row.id === distinctCurrent[0]) ?? winner)
      : winner;

  return {
    fundId: chosen.row.id,
    isin: chosen.row.isin ?? null,
    matchedOn: chosen.key,
    ambiguous,
    candidateFundIds: distinctIds.length > 1 ? distinctIds : [],
  };
}
